/**
 * ServiceFormDialog — add or edit a single service in the catalog.
 * Implements mockup SC5 with 4 collapsible sections.
 */
import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'

import { useUpsertServices } from '@/api/service-catalog'
import type { ServiceDetail } from '@/lib/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ServiceFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'add' | 'edit'
  /** Pre-fill for edit mode */
  service?: ServiceDetail | null
  /** Called after a successful save */
  onSuccess?: () => void
}

interface FormErrors {
  name?: string
  description?: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Split comma-separated text into a trimmed string array (empty items removed) */
function csvToArray(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

/** Join a string array into comma-separated text */
function arrayToCsv(arr: string[] | null | undefined): string {
  return arr?.join(', ') ?? ''
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ServiceFormDialog({
  open,
  onOpenChange,
  mode,
  service,
  onSuccess,
}: ServiceFormDialogProps) {
  const upsertMutation = useUpsertServices()

  // --- Form state (useState per field) ---
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [purpose, setPurpose] = useState('')
  const [country, setCountry] = useState('')

  // Ownership
  const [ownerTeam, setOwnerTeam] = useState('')
  const [ownerContacts, setOwnerContacts] = useState('')
  const [areaPath, setAreaPath] = useState('')

  // Technical
  const [repository, setRepository] = useState('')
  const [wikiPage, setWikiPage] = useState('')
  const [techStack, setTechStack] = useState('')
  const [dependencies, setDependencies] = useState('')
  const [components, setComponents] = useState('')

  // Discovery
  const [aliases, setAliases] = useState('')
  const [keywords, setKeywords] = useState('')
  const [keyFeatures, setKeyFeatures] = useState('')
  const [errorPatterns, setErrorPatterns] = useState('')

  // Validation
  const [errors, setErrors] = useState<FormErrors>({})

  // Collapsible section states (all expanded by default)
  const [basicOpen, setBasicOpen] = useState(true)
  const [ownershipOpen, setOwnershipOpen] = useState(true)
  const [technicalOpen, setTechnicalOpen] = useState(true)
  const [discoveryOpen, setDiscoveryOpen] = useState(true)

  // --- Pre-fill on open ---
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && service) {
        setName(service.name)
        setDescription(service.description ?? '')
        setPurpose(service.purpose ?? '')
        setCountry(service.country ?? '')
        setOwnerTeam(service.ownerTeam ?? '')
        setOwnerContacts(arrayToCsv(service.ownerContacts))
        setAreaPath(service.areaPath ?? '')
        setRepository(service.repository ?? '')
        setWikiPage(service.wikiPage ?? '')
        setTechStack(arrayToCsv(service.techStack))
        setDependencies(arrayToCsv(service.dependencies))
        setComponents(arrayToCsv(service.components))
        setAliases(arrayToCsv(service.aliases))
        setKeywords(arrayToCsv(service.keywords))
        setKeyFeatures(arrayToCsv(service.keyFeatures))
        setErrorPatterns(arrayToCsv(service.errorPatterns))
      } else {
        resetForm()
      }
      setErrors({})
    }
  }, [open, mode, service])

  // --- Helpers ---

  function resetForm() {
    setName('')
    setDescription('')
    setPurpose('')
    setCountry('')
    setOwnerTeam('')
    setOwnerContacts('')
    setAreaPath('')
    setRepository('')
    setWikiPage('')
    setTechStack('')
    setDependencies('')
    setComponents('')
    setAliases('')
    setKeywords('')
    setKeyFeatures('')
    setErrorPatterns('')
    setErrors({})
  }

  function validate(): boolean {
    const newErrors: FormErrors = {}
    if (!name.trim()) newErrors.name = 'Name is required'
    if (!description.trim()) newErrors.description = 'Description is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  async function handleSubmit() {
    if (!validate()) return

    const serviceData: ServiceDetail = {
      name: name.trim(),
      description: description.trim(),
      purpose: purpose.trim() || null,
      country: country.trim() || null,
      ownerTeam: ownerTeam.trim() || null,
      ownerContacts: csvToArray(ownerContacts),
      areaPath: areaPath.trim() || null,
      repository: repository.trim() || null,
      wikiPage: wikiPage.trim() || null,
      techStack: csvToArray(techStack),
      dependencies: csvToArray(dependencies),
      components: csvToArray(components),
      aliases: csvToArray(aliases),
      keywords: csvToArray(keywords),
      keyFeatures: csvToArray(keyFeatures),
      errorPatterns: csvToArray(errorPatterns),
    }

    try {
      await upsertMutation.mutateAsync([serviceData])
      toast.success(
        mode === 'add'
          ? `Service "${serviceData.name}" created`
          : `Service "${serviceData.name}" updated`
      )
      resetForm()
      onSuccess?.()
      onOpenChange(false)
    } catch {
      toast.error('Failed to save service')
    }
  }

  function handleCancel() {
    resetForm()
    onOpenChange(false)
  }

  // --- Render ---

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'add' ? 'Add Service' : 'Edit Service'}</DialogTitle>
          <DialogDescription>
            {mode === 'add'
              ? 'Add a new service to the catalog.'
              : 'Update the service details.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* ── Basic Info ── */}
          <CollapsibleSection
            title="Basic Info"
            open={basicOpen}
            onOpenChange={setBasicOpen}
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="svc-name">Name *</Label>
                <Input
                  id="svc-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={mode === 'edit'}
                  placeholder="e.g. AuthService"
                />
                {errors.name && (
                  <p className="text-sm text-destructive mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="svc-description">Description *</Label>
                <Textarea
                  id="svc-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Short description of the service"
                  rows={3}
                />
                {errors.description && (
                  <p className="text-sm text-destructive mt-1">{errors.description}</p>
                )}
              </div>

              <div>
                <Label htmlFor="svc-purpose">Purpose</Label>
                <Input
                  id="svc-purpose"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder="Primary purpose of the service"
                />
              </div>

              <div>
                <Label htmlFor="svc-country">Country</Label>
                <Input
                  id="svc-country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="e.g. DE"
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* ── Ownership ── */}
          <CollapsibleSection
            title="Ownership"
            open={ownershipOpen}
            onOpenChange={setOwnershipOpen}
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="svc-ownerTeam">Owner Team</Label>
                <Input
                  id="svc-ownerTeam"
                  value={ownerTeam}
                  onChange={(e) => setOwnerTeam(e.target.value)}
                  placeholder="e.g. Platform Team"
                />
              </div>

              <div>
                <Label htmlFor="svc-ownerContacts">Owner Contacts</Label>
                <Input
                  id="svc-ownerContacts"
                  value={ownerContacts}
                  onChange={(e) => setOwnerContacts(e.target.value)}
                  placeholder="comma-separated emails"
                />
              </div>

              <div>
                <Label htmlFor="svc-areaPath">Area Path</Label>
                <Input
                  id="svc-areaPath"
                  value={areaPath}
                  onChange={(e) => setAreaPath(e.target.value)}
                  placeholder="e.g. Project\\Auth"
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* ── Technical ── */}
          <CollapsibleSection
            title="Technical"
            open={technicalOpen}
            onOpenChange={setTechnicalOpen}
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="svc-repository">Repository</Label>
                <Input
                  id="svc-repository"
                  value={repository}
                  onChange={(e) => setRepository(e.target.value)}
                  placeholder="Repository URL"
                />
              </div>

              <div>
                <Label htmlFor="svc-wikiPage">Wiki Page</Label>
                <Input
                  id="svc-wikiPage"
                  value={wikiPage}
                  onChange={(e) => setWikiPage(e.target.value)}
                  placeholder="Wiki URL"
                />
              </div>

              <div>
                <Label htmlFor="svc-techStack">Tech Stack</Label>
                <Input
                  id="svc-techStack"
                  value={techStack}
                  onChange={(e) => setTechStack(e.target.value)}
                  placeholder="comma-separated, e.g. C#, .NET 8, Redis"
                />
              </div>

              <div>
                <Label htmlFor="svc-dependencies">Dependencies</Label>
                <Input
                  id="svc-dependencies"
                  value={dependencies}
                  onChange={(e) => setDependencies(e.target.value)}
                  placeholder="comma-separated"
                />
              </div>

              <div>
                <Label htmlFor="svc-components">Components</Label>
                <Input
                  id="svc-components"
                  value={components}
                  onChange={(e) => setComponents(e.target.value)}
                  placeholder="comma-separated"
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* ── Discovery ── */}
          <CollapsibleSection
            title="Discovery"
            open={discoveryOpen}
            onOpenChange={setDiscoveryOpen}
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="svc-aliases">Aliases</Label>
                <Input
                  id="svc-aliases"
                  value={aliases}
                  onChange={(e) => setAliases(e.target.value)}
                  placeholder="comma-separated"
                />
              </div>

              <div>
                <Label htmlFor="svc-keywords">Keywords</Label>
                <Input
                  id="svc-keywords"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="comma-separated"
                />
              </div>

              <div>
                <Label htmlFor="svc-keyFeatures">Key Features</Label>
                <Input
                  id="svc-keyFeatures"
                  value={keyFeatures}
                  onChange={(e) => setKeyFeatures(e.target.value)}
                  placeholder="comma-separated"
                />
              </div>

              <div>
                <Label htmlFor="svc-errorPatterns">Error Patterns</Label>
                <Input
                  id="svc-errorPatterns"
                  value={errorPatterns}
                  onChange={(e) => setErrorPatterns(e.target.value)}
                  placeholder="comma-separated"
                />
              </div>
            </div>
          </CollapsibleSection>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={upsertMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={upsertMutation.isPending}>
            {upsertMutation.isPending ? 'Saving...' : 'Save Service'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Collapsible Section helper
// ---------------------------------------------------------------------------

function CollapsibleSection({
  title,
  open,
  onOpenChange,
  children,
}: {
  title: string
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
}) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center gap-2 px-0 hover:bg-transparent w-full justify-start"
        >
          {open ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <span className="text-sm font-medium">{title}</span>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2 space-y-4 pl-6">
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}
